import { htmlSafe } from '@ember/template';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import formatCurrencySuperscriptCents from '../../helpers/format-currency-superscript-cents';
import { formatISODateToDisplay } from '../../utils/date';
import Popover from 'tio-reimagined/components/popover';
import type { IntlService } from 'ember-intl';
import type { LimitsStatusDataSignature } from '../../services/tuition-assistance';

interface BenefitHistorySummarySignature {
  Args: {
    limitsStatusPrevious?: LimitsStatusDataSignature;
    limitsStatusCurrent?: LimitsStatusDataSignature;
    limitsStatusNext?: LimitsStatusDataSignature;
    externalSpendLabel: string;
  };
}

export default class BenefitHistorySummary extends Component<BenefitHistorySummarySignature> {
  @service declare intl: IntlService;

  get minVisiblePercentage() {
    return 5;
  }

  get shouldShowNextYear() {
    const nextStatus = this.args.limitsStatusNext;
    if (!nextStatus?.annual_start_date) {
      return false;
    }

    const nextStart = new Date(nextStatus.annual_start_date);
    if (Number.isNaN(nextStart.getTime())) {
      return false;
    }

    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() + 6);
    return nextStart <= cutoff;
  }

  get summaryCards() {
    const cards = [
      {
        label: this.intl.t('tas.benefit_history.last_year'),
        status: this.args.limitsStatusPrevious,
      },
      {
        label: this.intl.t('tas.benefit_history.current_year'),
        status: this.args.limitsStatusCurrent,
      },
    ];

    if (this.shouldShowNextYear) {
      cards.push({
        label: this.intl.t('tas.benefit_history.next_year'),
        status: this.args.limitsStatusNext,
      });
    }

    return cards;
  }

  get summaryGridClass() {
    const base = 'grid gap-4';
    const count = this.summaryCards.length;

    if (count <= 1) {
      return `${base} md:grid-cols-1`;
    }

    if (count === 2) {
      return `${base} md:grid-cols-2`;
    }

    return `${base} md:grid-cols-3`;
  }

  getAnnualLimit = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const batchLimit = status.details?.batch?.annual_limit ?? 0;
    const schemeLimit = status.details?.scheme?.annual_limit ?? 0;
    const productLimit = status.details?.product?.annual_limit ?? 0;
    const companyLimit = status.details?.company?.annual_limit ?? 0;

    return Math.max(batchLimit, schemeLimit, productLimit, companyLimit);
  };

  getPaidAmount = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const batchUsage = status.details?.batch?.annual_usage ?? 0;
    const schemeUsage = status.details?.scheme?.annual_usage ?? 0;
    const productUsage = status.details?.product?.annual_usage ?? 0;
    const companyUsage = status.details?.company?.annual_usage ?? 0;

    return Math.max(batchUsage, schemeUsage, productUsage, companyUsage);
  };

  getExternalAmount = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const batchExternal = status.details?.batch?.external_annual_usage ?? 0;
    const schemeExternal = status.details?.scheme?.external_annual_usage ?? 0;
    const productExternal = status.details?.product?.external_annual_usage ?? 0;
    const companyExternal = status.details?.company?.external_annual_usage ?? 0;
    const rootExternal = status.external_annual_usage ?? 0;

    return Math.max(batchExternal, schemeExternal, productExternal, companyExternal, rootExternal);
  };

  getTotalUsed = (status?: LimitsStatusDataSignature) => {
    return this.getPaidAmount(status) + this.getExternalAmount(status);
  };

  hasExternalSpend = (status?: LimitsStatusDataSignature) => {
    return this.getExternalAmount(status) > 0;
  };

  hasAnnualLimit = (status?: LimitsStatusDataSignature) => {
    return this.getAnnualLimit(status) > 0;
  };

  getRemainingAmount = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const available = status.available;
    if (available !== null && available !== undefined) {
      // Server's `available` = annual_limit - annual_usage, but does NOT subtract
      // external spend. Subtract it here so the display is accurate.
      return Math.max(0, available - this.getExternalAmount(status));
    }

    const annualLimit = this.getAnnualLimit(status);
    if (!annualLimit) return 0;
    return Math.max(0, annualLimit - this.getTotalUsed(status));
  };

  getPaidPercentage = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const annualLimit = this.getAnnualLimit(status);
    const totalUsed = this.getTotalUsed(status);
    if (!annualLimit || totalUsed <= 0) return 0;
    const rawPercentage = (totalUsed / annualLimit) * 100;
    return Math.min(100, Math.max(this.minVisiblePercentage, rawPercentage));
  };

  getTotalUsedPercentage = (status?: LimitsStatusDataSignature) => {
    return this.getPaidPercentage(status);
  };

  getPaidSegmentPercentage = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const totalUsed = this.getTotalUsed(status);
    const paidAmount = this.getPaidAmount(status);
    if (!this.hasAnnualLimit(status) || totalUsed <= 0 || paidAmount <= 0) return 0;
    return (paidAmount / totalUsed) * this.getTotalUsedPercentage(status);
  };

  getExternalSegmentPercentage = (status?: LimitsStatusDataSignature) => {
    if (!status) return 0;
    const totalUsed = this.getTotalUsed(status);
    const externalAmount = this.getExternalAmount(status);
    if (!this.hasAnnualLimit(status) || totalUsed <= 0 || externalAmount <= 0) return 0;
    return (externalAmount / totalUsed) * this.getTotalUsedPercentage(status);
  };

  getPaidStyle = (status?: LimitsStatusDataSignature) => {
    return htmlSafe(`width: ${this.getPaidSegmentPercentage(status)}%`);
  };

  getExternalStyle = (status?: LimitsStatusDataSignature) => {
    return htmlSafe(`width: ${this.getExternalSegmentPercentage(status)}%`);
  };

  formatAmount = (cents: number) => {
    return this.intl.formatNumber(cents / 100, { style: 'currency', currency: 'USD' });
  };

  getPaidLabel = () => {
    return this.intl.t('tas.dashboard.annual_benefits.paid') as string;
  };

  getPaidAmount_formatted = (status?: LimitsStatusDataSignature) => {
    return this.formatAmount(this.getPaidAmount(status));
  };

  getExternalAmount_formatted = (status?: LimitsStatusDataSignature) => {
    return this.formatAmount(this.getExternalAmount(status));
  };

  getAnnualStartDate = (status?: LimitsStatusDataSignature) => {
    return status?.annual_start_date || '';
  };

  getAnnualEndDate = (status?: LimitsStatusDataSignature) => {
    return status?.annual_end_date || '';
  };

  <template>
    <div class="flex flex-col gap-4">
      <div class={{this.summaryGridClass}}>
        {{#each this.summaryCards as |card|}}
          <div class="border border-gray-200 rounded-xl p-4">
            <div class="text-sm text-gray-500 font-semibold">{{card.label}}</div>
            {{#if card.status}}
              {{#if (this.hasAnnualLimit card.status)}}
                <div class="text-2xl font-semibold text-gray-900 mt-2">
                  {{formatCurrencySuperscriptCents (this.getRemainingAmount card.status)}}
                </div>
                <div class="text-xs text-gray-500">
                  {{t "tas.dashboard.annual_benefits.estimated_remaining_benefit"}}
                </div>
                <div class="mt-3">
                  <div class="w-full h-2 bg-gray-200 rounded-full overflow-hidden flex">
                    {{#if (this.getPaidSegmentPercentage card.status)}}
                      <Popover
                        @placement="top"
                        @triggerMode="hover"
                        @maxWidth={{200}}
                        @closeOnScroll={{false}}
                      >
                        <:trigger as |p|>
                          <div
                            class="h-full bg-violet-700 cursor-pointer"
                            style={{this.getPaidStyle card.status}}
                            role="button"
                            tabindex="0"
                            {{p.register}}
                            {{p.hoverHandlers}}
                            {{on "click" p.toggle}}
                          ></div>
                        </:trigger>
                        <:body>
                          <span class="font-semibold">
                            {{this.getPaidLabel}}
                          </span>:
                          {{this.getPaidAmount_formatted card.status}}
                        </:body>
                      </Popover>
                    {{/if}}
                    {{#if (this.getExternalSegmentPercentage card.status)}}
                      <Popover
                        @placement="top"
                        @triggerMode="hover"
                        @maxWidth={{200}}
                        @closeOnScroll={{false}}
                      >
                        <:trigger as |p|>
                          <div
                            class="h-full bg-cyan-500 cursor-pointer"
                            style={{this.getExternalStyle card.status}}
                            role="button"
                            tabindex="0"
                            {{p.register}}
                            {{p.hoverHandlers}}
                            {{on "click" p.toggle}}
                          ></div>
                        </:trigger>
                        <:body>
                          <span class="font-semibold">{{@externalSpendLabel}}</span>:
                          {{this.getExternalAmount_formatted card.status}}
                        </:body>
                      </Popover>
                    {{/if}}
                  </div>
                  <div
                    class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-2"
                  >
                    <div class="flex items-center gap-1.5">
                      <span class="w-2 h-2 rounded-full bg-violet-700"></span>
                      <span class="text-gray-700">
                        {{t "tas.dashboard.annual_benefits.paid"}}
                      </span>
                    </div>
                    {{#if (this.hasExternalSpend card.status)}}
                      <div class="flex items-center gap-1.5">
                        <span class="w-2 h-2 rounded-full bg-cyan-500"></span>
                        <span class="text-gray-700">{{@externalSpendLabel}}</span>
                      </div>
                    {{/if}}
                  </div>
                  <div class="flex items-center justify-between text-xs text-gray-500 mt-2">
                    <span class="font-semibold">
                      {{formatISODateToDisplay
                        (this.getAnnualStartDate card.status)
                        this.intl
                        "2-digit"
                      }}
                      -
                      {{formatISODateToDisplay
                        (this.getAnnualEndDate card.status)
                        this.intl
                        "2-digit"
                      }}
                    </span>
                    <span>
                      {{t "tas.dashboard.annual_benefits.eligible_up_to"}}
                      <span class="font-semibold">
                        {{formatCurrencySuperscriptCents (this.getAnnualLimit card.status)}}
                      </span>
                    </span>
                  </div>
                </div>
              {{else}}
                <div class="text-2xl font-semibold text-gray-900 mt-2">
                  {{formatCurrencySuperscriptCents (this.getTotalUsed card.status)}}
                </div>
                <div class="text-xs text-gray-500">
                  {{t "tas.dashboard.annual_benefits.total_used_this_year"}}
                </div>
                <div class="mt-3">
                  <div class="flex items-center justify-between text-xs text-gray-500 mt-2">
                    <span class="font-semibold">
                      {{formatISODateToDisplay
                        (this.getAnnualStartDate card.status)
                        this.intl
                        "2-digit"
                      }}
                      -
                      {{formatISODateToDisplay
                        (this.getAnnualEndDate card.status)
                        this.intl
                        "2-digit"
                      }}
                    </span>
                  </div>
                </div>
              {{/if}}
            {{else}}
              <div class="text-2xl font-semibold text-gray-400 mt-2">—</div>
            {{/if}}
          </div>
        {{/each}}
      </div>
    </div>
  </template>
}
